import { COLUMN_PRESETS, STANDARD_COLUMN_WIDTHS } from '../column-widths'
import { FieldTypeEnum } from '../custom-field.types'
import { ModuleColumnDefinition } from '../module'
import { ModuleTypeEnum } from '../module-type'
import { EntityTerminology } from '../organization-settings'
import { Ticket } from '../ticket'

export const TICKET_STANDARD_COLUMNS: ModuleColumnDefinition[] = [
  {
    id: 'subject',
    name: 'subject' as keyof Ticket,
    label: 'Subject',
    description: 'Ticket subject',
    isVisible: true,
    isRequired: true,
    isSystem: true,
    isFixed: true,
    order: 0,
    fieldType: FieldTypeEnum.enum.STRING,
    ...COLUMN_PRESETS.NAME,
  },
  {
    id: 'ticketNumber',
    name: 'ticketNumber' as keyof Ticket,
    label: 'Ticket #',
    description: 'Ticket number',
    isVisible: true,
    isRequired: false,
    isSystem: true,
    isFixed: false,
    isReadOnly: true,
    order: 1,
    fieldType: FieldTypeEnum.enum.STRING,
    width: '150px',
    minWidth: '110px',
  },
  {
    id: 'typeDefId',
    name: 'typeDefId' as keyof Ticket,
    label: 'Type',
    description: 'Ticket type',
    isVisible: true,
    isRequired: false,
    isSystem: false,
    isFixed: false,
    order: 2,
    fieldType: FieldTypeEnum.enum.DROPDOWN,
    ...STANDARD_COLUMN_WIDTHS.MEDIUM,
  },
  {
    id: 'client',
    name: 'client',
    label: 'Client',
    description: 'Linked client or entity',
    isVisible: true,
    isRequired: false,
    isSystem: false,
    isFixed: false,
    order: 3,
    fieldType: FieldTypeEnum.enum.REFERENCE,
    width: '240px',
    minWidth: '180px',
  },
  {
    id: 'requesterName',
    name: 'requesterName' as keyof Ticket,
    label: 'Requester',
    description: 'Requester name',
    isVisible: true,
    isRequired: false,
    isSystem: false,
    isFixed: false,
    order: 4,
    fieldType: FieldTypeEnum.enum.STRING,
    ...STANDARD_COLUMN_WIDTHS.WIDE,
  },
  {
    id: 'requesterEmail',
    name: 'requesterEmail' as keyof Ticket,
    label: 'Requester Email',
    description: 'Requester email address',
    isVisible: false,
    isRequired: false,
    isSystem: false,
    isFixed: false,
    order: 5,
    fieldType: FieldTypeEnum.enum.STRING,
    ...STANDARD_COLUMN_WIDTHS.WIDE,
  },
  {
    id: 'priority',
    name: 'priority' as keyof Ticket,
    label: 'Priority',
    description: 'Ticket priority',
    isVisible: true,
    isRequired: false,
    isSystem: false,
    isFixed: false,
    order: 6,
    fieldType: FieldTypeEnum.enum.DROPDOWN,
    ...COLUMN_PRESETS.PRIORITY,
  },
  {
    id: 'stage',
    name: 'stage' as keyof Ticket,
    label: 'Stage',
    description: 'Ticket lifecycle stage',
    isVisible: true,
    isRequired: false,
    isSystem: false,
    isFixed: false,
    order: 7,
    fieldType: FieldTypeEnum.enum.DROPDOWN,
    ...COLUMN_PRESETS.STATUS,
  },
  {
    id: 'assignees',
    name: 'assignees',
    label: 'Assignees',
    description: 'Users assigned to the ticket',
    isVisible: true,
    isRequired: false,
    isSystem: false,
    isFixed: false,
    order: 8,
    fieldType: FieldTypeEnum.enum.MULTI_USER,
    ...COLUMN_PRESETS.ASSIGNEES,
  },
  {
    id: 'source',
    name: 'source' as keyof Ticket,
    label: 'Source',
    description: 'Ticket source',
    isVisible: true,
    isRequired: false,
    isSystem: false,
    isFixed: false,
    order: 9,
    fieldType: FieldTypeEnum.enum.DROPDOWN,
    ...STANDARD_COLUMN_WIDTHS.NARROW,
  },
  {
    id: 'dueDate',
    name: 'dueDate' as keyof Ticket,
    label: 'Due Date',
    description: 'Ticket due date',
    isVisible: true,
    isRequired: false,
    isSystem: false,
    isFixed: false,
    order: 10,
    fieldType: FieldTypeEnum.enum.DATE,
    ...COLUMN_PRESETS.DUE_DATE,
  },
  {
    id: 'commentCount',
    name: 'commentCount' as keyof Ticket,
    label: 'Comments',
    description: 'Number of ticket comments',
    isVisible: true,
    isRequired: false,
    isSystem: true,
    isFixed: false,
    isReadOnly: true,
    order: 11,
    fieldType: FieldTypeEnum.enum.NUMBER,
    ...STANDARD_COLUMN_WIDTHS.NARROW,
  },
  {
    id: 'linkedTask',
    name: 'linkedTask' as keyof Ticket,
    label: 'Linked Tasks',
    description: 'Tasks created from this ticket, and how many are done',
    isVisible: false,
    isRequired: false,
    isSystem: true,
    isFixed: false,
    isReadOnly: true,
    order: 12,
    fieldType: FieldTypeEnum.enum.REFERENCE,
    width: '220px',
    minWidth: '160px',
  },
  {
    id: 'deployment',
    name: 'deployment' as keyof Ticket,
    label: 'Deployment',
    description: 'Deployments this ticket is included in',
    isVisible: false,
    isRequired: false,
    isSystem: true,
    isFixed: false,
    isReadOnly: true,
    order: 13,
    fieldType: FieldTypeEnum.enum.STRING,
    width: '200px',
    minWidth: '150px',
  },
  {
    id: 'csatRating',
    name: 'csatRating' as keyof Ticket,
    label: 'CSAT',
    description: 'Requester satisfaction rating (1 bad / 2 okay / 3 good)',
    isVisible: false,
    isRequired: false,
    isSystem: true,
    isFixed: false,
    isReadOnly: true,
    order: 14,
    fieldType: FieldTypeEnum.enum.NUMBER,
    ...STANDARD_COLUMN_WIDTHS.NARROW,
  },
  {
    id: 'slaDueAt',
    name: 'slaDueAt' as keyof Ticket,
    label: 'SLA',
    description: 'SLA status for the most urgent applicable timer',
    isVisible: false,
    isRequired: false,
    isSystem: true,
    isFixed: false,
    isReadOnly: true,
    order: 15,
    fieldType: FieldTypeEnum.enum.DATETIME,
    width: '160px',
    minWidth: '120px',
  },
  {
    id: 'createdBy',
    name: 'createdBy' as keyof Ticket,
    label: 'Created By',
    description: 'User who created the ticket',
    isVisible: false,
    isRequired: false,
    isSystem: true,
    isFixed: false,
    isReadOnly: true,
    order: 98,
    fieldType: FieldTypeEnum.enum.USER,
    ...STANDARD_COLUMN_WIDTHS.MEDIUM,
  },
  {
    id: 'createdAt',
    name: 'createdAt' as keyof Ticket,
    label: 'Created Date',
    description: 'Date ticket was created',
    isVisible: false,
    isRequired: false,
    isSystem: true,
    isFixed: false,
    order: 99,
    fieldType: FieldTypeEnum.enum.DATETIME,
    ...STANDARD_COLUMN_WIDTHS.MEDIUM,
  },
  {
    id: 'updatedAt',
    name: 'updatedAt' as keyof Ticket,
    label: 'Last Updated',
    description: 'Date ticket was last updated',
    isVisible: false,
    isRequired: false,
    isSystem: true,
    isFixed: false,
    order: 100,
    fieldType: FieldTypeEnum.enum.DATETIME,
    ...STANDARD_COLUMN_WIDTHS.MEDIUM,
  },
]

export function getTicketColumns(): ModuleColumnDefinition[] {
  return TICKET_STANDARD_COLUMNS
}

export function getTicketModuleType(): ModuleTypeEnum {
  return ModuleTypeEnum.TICKET
}

/**
 * Standard ticket columns with the org's entity terminology applied to the
 * linked-client column description. Falls back to the default columns.
 */
export function createTicketStandardColumns(
  terminology?: EntityTerminology,
): ModuleColumnDefinition[] {
  if (!terminology) return TICKET_STANDARD_COLUMNS
  return TICKET_STANDARD_COLUMNS.map(column =>
    column.id === 'client'
      ? { ...column, description: `Linked ${terminology.singular}` }
      : column,
  )
}
